import { VideoInfoRawData } from "../../../../model/video/VideoInfo.js";
import { MediaControlOrigin } from "../../../events/PlayerEvent.js";
import { GenericFormatLabel, ParsedFormat } from "../FormatAndSizePreference.js";
import { MediaEventName, VideoResource, MediaResourceStats } from "../../MediaResource.js";
import { NativeVODPlayer } from "./NativeVODPlayer.js";


/**
 * Represents a media resource for native VOD (Video On Demand) playback using an HTMLVideoElement.
 * Extends {@link VideoResource} and provides methods for controlling playback, volume, quality, and other video properties.
 *
 * @remarks
 * This class is designed to work with a {@link NativeVODPlayer} and manages the underlying video element,
 * including playback controls, event handling, and resource management.
 *
 * @example
 * ```typescript
 * const resource = new NativeVODPlayerMediaResource(player, videoElement, listItem, info, duration, stats, qualities);
 * resource.play();
 * resource.volume(0.5);
 * resource.switchQuality('720p');
 * ```
 *
 * @param player - The native VOD player instance.
 * @param video - The HTMLVideoElement associated with the resource.
 * @param listItem - The HTML element representing the list item for the video.
 * @param info - Raw video information data.
 * @param fixedDuration - The fixed duration of the video.
 * @param stats - Media resource statistics.
 * @param qualities - Array of available video qualities.
 *
 * @method loop - Gets or sets the loop property of the video element.
 * @method ensureResourceLoad - Ensures the media resource is loaded.
 * @method destroy - Cleans up the resource and resets the video element.
 * @method volume - Gets or sets the volume of the video element.
 * @method muted - Gets or sets the muted state of the video element.
 * @method autoplay - Gets or sets the autoplay property of the video element.
 * @method show - Makes the video element visible and sets inline playback attributes.
 * @method hide - Hides the video element and removes inline playback attributes.
 * @method on - Adds an event listener to the video element.
 * @method off - Removes an event listener from the video element.
 * @method beforeUnload - Pauses the video before unloading the resource.
 * @method switchQuality - Switches the video quality to the specified format label.
 * @method reloadSource - Reloads the video source, optionally keeping the current time.
 * @method seek - Seeks to a specific time in the video.
 * @method getCurrentFormat - Gets the current video format.
 *
 * @property currentTime - Gets the current playback time.
 * @property internalDuration - Gets the duration of the video.
 * @property ended - Indicates whether the video has ended.
 * @property isPaused - Indicates whether the video is paused.
 */
export class NativeVODPlayerMediaResource extends VideoResource<NativeVODPlayer> {


    constructor(
        player: NativeVODPlayer,
        video: HTMLVideoElement,
        listItem: HTMLElement,
        info: VideoInfoRawData,
        fixedDuration: number,
        stats: MediaResourceStats,
        qualities: ParsedFormat[]
    ) {
        super(player, video, listItem, info, fixedDuration, stats, qualities);
    }
    loop(loopEnabled?: boolean): boolean {
        if (!(typeof loopEnabled == "undefined")) {
            this.element.loop = loopEnabled;
        }
        return this.element.loop;
    }



    ensureResourceLoad(): Promise<void> {
        return this.player.load();
    }


    destroy(): void {
        this.element.pause();
        this.player.dispose();
        this.element.currentTime = 0;

    }

    get currentTime(): number {
        if (this.player.hasBeenLoaded()) {
            return this.element.currentTime;
        }
        else return 0;
    }
    get internalDuration(): number {
        if (this.player.hasBeenLoaded()) {
            return this.element.duration;
        }
        else return 0;
    }


    get ended(): boolean {
        if (this.player.hasBeenLoaded()) {
            return this.element.ended;
        }
        else return false;
    }

    get isPaused(): boolean {
        if (this.player.hasBeenLoaded()) {
            return this.element.paused;
        }
        else return true;
    }
    volume(volume?: number): number {
        if (!(typeof volume == "undefined")) {
            if (volume == 0) {
                this.muted(true);
            }
            this.element.volume = volume;

        }
        return this.element.volume;
    }
    muted(muted?: boolean): boolean {
        if (!(typeof muted == "undefined")) {
            this.element.muted = muted;
        }
        return this.element.muted;
    }
    autoplay(autoplay?: boolean): boolean {
        if (!(typeof autoplay == "undefined")) {
            this.element.autoplay = autoplay;
        }
        return this.element.autoplay;
    }
    protected internalPlay(): Promise<any> {
        return this.player.play();
    }

    protected internalRestartOperations(): Promise<any> {
        this.element.currentTime = 0
        return this.play(MediaControlOrigin.USER)
    }

    protected internalResetOperations(): Promise<any> {
        this.element.currentTime = 0
        return this.pause(MediaControlOrigin.USER)
    }

    protected internalPause(): Promise<any> {
        return Promise.resolve(this.element.pause());
    }
    show(): void {
        this.element.style.display = "block";
        this.element.playsInline = true;
        this.element.setAttribute("webkit-playsinline", "");
    }
    hide(): void {
        this.element.style.display = "none";
        this.element.playsInline = false;
        this.element.removeAttribute("webkit-playsinline");
    }

    on(eventName: MediaEventName, listener: EventListenerOrEventListenerObject): void {
        this.element.addEventListener(eventName, listener);
    }
    off(eventName: MediaEventName, listener: EventListenerOrEventListenerObject): void {
        this.element.removeEventListener(eventName, listener);
    }


    beforeUnload(): Promise<void> {
        this.element.pause();
        return Promise.resolve();
    }


    switchQuality(formatLabel: GenericFormatLabel): Promise<ParsedFormat> {
        let rProm: Promise<ParsedFormat> = Promise.resolve(null);
        const avq = this.getAvailableQualities();
        if (avq) {
            const found = avq.find(e => e.label == formatLabel);
            if (found) {
                rProm = this.player.updateFormat(found).then(_ => this.element.play()).then(_ => found)
            }
        }

        return rProm;
    }

    reloadSource(keepCurrentTime: boolean): Promise<void> {
        return this.player.reloadSource(keepCurrentTime);
    }

    seek(time: number): Promise<any> {
        this.element.currentTime = time;
        return Promise.resolve();
    }

    getCurrentFormat(): ParsedFormat {
        return this.player.getCurrentFormat();
    }


}
